'use client'

import { getCurrentUserConnectedAccounts } from '@/actions/user'
import {
  getApplicationItem,
  getApplicationQuestionCategory,
} from '@/components/_registration/application/form-components'
import ImageWithFallback from '@/components/common/image-with-fallback'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatApplicationQuestionKey } from '@/lib/advancement/application-transfer-policy'
import {
  isEmptyValue,
  normalizeFormValues,
  validateApplication,
} from '@/lib/application/application-validation'
import { formatDurationCountdown } from '@/lib/countdown'
import dayjs from '@/lib/dayjs'
import {
  canRecruitForEvent,
  getEffectiveRecruitmentOpenDateTime,
} from '@/lib/recruitment-scheduling'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormStatus } from 'react-dom'
import toast from 'react-hot-toast'

const eventIntentOptions = {
  registered: {
    label: "I'm attending",
    triggerClass:
      'bg-success-500/10 border-success-500/20 text-success-400 hover:bg-success-500/20 data-[state=open]:border-success-500/50',
    itemClass:
      'text-success-400 focus:bg-success-500/10 focus:text-success-400',
  },
  reserve: {
    label: 'Reserve',
    triggerClass:
      'bg-warning-500/10 border-warning-500/20 text-warning-400 hover:bg-warning-500/20 data-[state=open]:border-warning-500/50',
    itemClass:
      'text-warning-400 focus:bg-warning-500/10 focus:text-warning-400',
  },
  withdrawn: {
    label: 'Not attending',
    triggerClass:
      'bg-error-500/10 border-error-500/20 text-error-400 hover:bg-error-500/20 data-[state=open]:border-error-500/50',
    itemClass: 'text-error-400 focus:bg-error-500/10 focus:text-error-400',
  },
}

function useEventRegistrationCountdown(championship, event) {
  const [now, setNow] = useState(null)
  const registrationOpenDateTime = getEffectiveRecruitmentOpenDateTime(
    championship,
    event,
  )
  const registrationOpenTimestamp = registrationOpenDateTime
    ? new Date(registrationOpenDateTime).getTime()
    : null
  const hasValidRegistrationOpenTimestamp = Number.isFinite(
    registrationOpenTimestamp,
  )

  useEffect(() => {
    if (!hasValidRegistrationOpenTimestamp) return

    const currentNow = Date.now()
    setNow(currentNow)

    if (registrationOpenTimestamp <= currentNow) return

    const intervalId = window.setInterval(() => {
      const nextNow = Date.now()
      setNow(nextNow)

      if (registrationOpenTimestamp <= nextNow) {
        window.clearInterval(intervalId)
      }
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [hasValidRegistrationOpenTimestamp, registrationOpenTimestamp])

  if (!hasValidRegistrationOpenTimestamp || !now) {
    return {
      isScheduled: false,
      label: null,
    }
  }

  if (registrationOpenTimestamp <= now) {
    return {
      isScheduled: false,
      label: null,
    }
  }

  return {
    isScheduled: true,
    label: formatDurationCountdown(registrationOpenTimestamp - now),
  }
}

export default function ApplicationDialog({
  championshipId,
  championship,
  applicationQuestions,
  isApplicationFormEnabled,
  futureEvents,
  formAction,
  open,
  setOpen,
  carClasses,
  platforms,
  user,
  initialApplicationAnswers = {},
  applicationTransfer = null,
}) {
  const [formValues, setFormValues] = useState({})
  const [accountsLoading, setAccountsLoading] = useState([])
  const [showErrors, setShowErrors] = useState(false)
  const [step, setStep] = useState(1)
  const [eventIntentById, setEventIntentById] = useState({})

  useEffect(() => {
    if (!open) return
    setFormValues((previousValues) => ({
      ...initialApplicationAnswers,
      ...previousValues,
    }))
  }, [initialApplicationAnswers, open])

  useEffect(() => {
    if (!open || !user) return

    const fullName = [user.firstName, user.lastName]
      .filter(Boolean)
      .join(' ')
      .trim()

    setFormValues((prevValues) => {
      const isDiscordAutoFill =
        isEmptyValue(prevValues.discord_username) && !!user.discordUsername

      return {
        ...prevValues,
        email: isEmptyValue(prevValues.email)
          ? user.email || ''
          : prevValues.email,
        real_name: isEmptyValue(prevValues.real_name)
          ? fullName || ''
          : prevValues.real_name,
        discord_username: isDiscordAutoFill
          ? user.discordUsername || ''
          : prevValues.discord_username,
        ...(isDiscordAutoFill && {
          _autoFilled: {
            ...prevValues._autoFilled,
            discord_username: true,
          },
        }),
      }
    })
  }, [open, user])

  useEffect(() => {
    if (!open) return

    setShowErrors(false)

    setEventIntentById(
      Object.fromEntries(
        (futureEvents || []).map((event) => [
          event.id,
          canRecruitForEvent(championship, event) ? 'registered' : 'withdrawn',
        ]),
      ),
    )
  }, [championship, futureEvents, open])

  useEffect(() => {
    const fetchConnectedAccounts = async () => {
      try {
        const accountsToFetch = []

        if (
          applicationQuestions.some(
            (question) => question.key === 'steam_username',
          )
        ) {
          accountsToFetch.push('steam')
        }

        if (
          applicationQuestions.some((question) => question.key === 'iracing_id')
        ) {
          accountsToFetch.push('iracing')
        }

        setAccountsLoading(accountsToFetch)

        const accounts = await getCurrentUserConnectedAccounts(accountsToFetch)

        const newFormValues = {}

        const steamAccount = accounts.find(
          (account) => account.serviceId === 'steam',
        )

        if (steamAccount && steamAccount.serviceAccountId) {
          const id = steamAccount.serviceAccountId
          newFormValues.steam_username = id
          newFormValues._autoFilled = newFormValues._autoFilled || {}
          newFormValues._autoFilled.steam_username = true
        }

        const iracingAccount = accounts.find(
          (account) => account.serviceId === 'iracing',
        )

        if (iracingAccount && iracingAccount.serviceAccountId) {
          const id = iracingAccount.serviceAccountId
          newFormValues.iracing_id = id
          newFormValues._autoFilled = newFormValues._autoFilled || {}
          newFormValues._autoFilled.iracing_id = true
        }

        const discordAccount = accounts.find(
          (account) => account.serviceId === 'discord',
        )

        if (discordAccount && discordAccount.metadata?.discord?.username) {
          newFormValues.discord_username =
            discordAccount.metadata.discord.username
          newFormValues._autoFilled = newFormValues._autoFilled || {}
          newFormValues._autoFilled.discord_username = true
        }

        // Auto-fill car class and platform if there's only one option
        if (carClasses?.length === 1) {
          const carClass = carClasses[0]
          newFormValues.car_class =
            carClass?.id ?? carClass?.carClass?.id ?? carClass?.name ?? null
          newFormValues._autoFilled = newFormValues._autoFilled || {}
          newFormValues._autoFilled.car_class = true
        }

        if (platforms?.length === 1) {
          const platform = platforms[0]
          newFormValues.platform =
            platform?.name ??
            platform?.platformName ??
            (typeof platform === 'string' ? platform : null)
          newFormValues._autoFilled = newFormValues._autoFilled || {}
          newFormValues._autoFilled.platform = true
        }

        setFormValues((prevValues) => ({
          ...prevValues,
          ...newFormValues,
        }))
      } catch (error) {
        console.error('Error fetching connected accounts:', error)
      } finally {
        setAccountsLoading([])
      }
    }

    if (open) {
      fetchConnectedAccounts()
    }
  }, [applicationQuestions, open, carClasses, platforms])

  const setFormValue = useCallback((key, value) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [key]: value,
    }))
  }, [])

  const gameQuestions = useMemo(() => {
    return applicationQuestions.filter((question) => {
      return getApplicationQuestionCategory(question.key) === 'game'
    })
  }, [applicationQuestions])

  const driverQuestions = useMemo(() => {
    return applicationQuestions.filter((question) => {
      return getApplicationQuestionCategory(question.key) === 'driver'
    })
  }, [applicationQuestions])

  const connectQuestions = useMemo(() => {
    return applicationQuestions.filter((question) => {
      return getApplicationQuestionCategory(question.key) === 'connect'
    })
  }, [applicationQuestions])

  const validationErrors = useMemo(
    () => validateApplication(applicationQuestions, formValues),
    [applicationQuestions, formValues],
  )

  const hasAnyAnswers = useMemo(
    () =>
      Object.entries(formValues).some(([key, value]) => {
        if (key === '_autoFilled') return false
        return !isEmptyValue(value)
      }),
    [formValues],
  )

  const hasValidationErrors = Object.keys(validationErrors).length > 0
  const hasQuestions = applicationQuestions.length > 0

  // Calculate if the user has any interactive questions left to answer manually
  const hasInteractiveQuestions = useMemo(() => {
    return applicationQuestions.some((q) => {
      if (q.key === 'car_class' && carClasses?.length === 1) return false
      if (q.key === 'platform' && platforms?.length === 1) return false
      return true
    })
  }, [applicationQuestions, carClasses, platforms])

  const canProceedFromStepOne =
    !hasQuestions || (!hasValidationErrors && hasAnyAnswers)

  useEffect(() => {
    if (!open) return

    // Auto-advance if we only have auto-filled questions or no questions at all
    if (!hasQuestions || (canProceedFromStepOne && !hasInteractiveQuestions)) {
      setStep(2)
    } else {
      setStep(1)
    }
  }, [open, canProceedFromStepOne, hasQuestions, hasInteractiveQuestions])

  const normalizedAnswers = useMemo(
    () => normalizeFormValues(formValues),
    [formValues],
  )

  const eventIntent = useMemo(
    () =>
      (futureEvents || []).map((event) => ({
        eventId: event.id,
        intent: eventIntentById[event.id] || 'registered',
      })),
    [eventIntentById, futureEvents],
  )
  const eventCount = (futureEvents || []).length
  const isSingleEventFlow = eventCount === 1
  const getIntentTriggerClass = useCallback(
    (eventId) => {
      const intent = eventIntentById[eventId] || 'registered'
      return eventIntentOptions[intent]?.triggerClass || ''
    },
    [eventIntentById],
  )

  const onContinue = () => {
    setShowErrors(true)

    if (!canProceedFromStepOne) {
      toast.error('Please fix the highlighted fields before continuing.', {
        id: 'application-required',
      })
      return
    }

    setStep(2)
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => setOpen(isOpen)}>
      <DialogContent className="w-11/12 p-5 lg:w-[700px]">
        <DialogHeader>
          <DialogTitle>
            {step === 1
              ? isApplicationFormEnabled
                ? 'Application Questions'
                : 'Required Driver Details'
              : isApplicationFormEnabled && hasQuestions
                ? isSingleEventFlow
                  ? 'Event Attendance'
                  : 'Event Attendance Intent'
                : 'Confirm Registration'}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? isApplicationFormEnabled
                ? 'Step 1 of 2: answer your championship application questions.'
                : 'Step 1 of 2: provide the required details for your registration.'
              : isApplicationFormEnabled && hasQuestions
                ? isSingleEventFlow
                  ? 'Step 2 of 2: tell us if you plan to race this event.'
                  : 'Step 2 of 2: tell us which events you plan to race.'
                : isSingleEventFlow
                  ? 'Tell us if you plan to race this event.'
                  : 'Tell us which events you plan to race.'}
          </DialogDescription>
        </DialogHeader>

        <div className="divider my-0.5!" />

        <form action={formAction}>
          <input type="hidden" name="championshipId" value={championshipId} />
          <input
            type="hidden"
            name="applicationAnswers"
            value={JSON.stringify(normalizedAnswers)}
          />
          <input
            type="hidden"
            name="eventIntent"
            value={JSON.stringify(eventIntent)}
          />

          {step === 1 ? (
            <>
              {applicationTransfer?.carriedAnswerCount > 0 && (
                <div className="mb-3 rounded-lg border border-sky-500/30 bg-sky-500/10 p-3 text-sm text-neutral-blue-100">
                  <p className="font-semibold">
                    We carried {applicationTransfer.carriedAnswerCount}{' '}
                    {applicationTransfer.carriedAnswerCount === 1
                      ? 'answer'
                      : 'answers'}{' '}
                    from {applicationTransfer.sourceChampionshipName}
                  </p>
                  <p className="mt-1 text-neutral-blue-200">
                    Review the prefilled details before submitting.
                    {applicationTransfer.requiresConfirmationKeys.length > 0 &&
                      ` Please complete ${(
                        applicationTransfer.requiresConfirmationLabels ||
                        applicationTransfer.requiresConfirmationKeys
                      )
                        .map(formatApplicationQuestionKey)
                        .join(', ')} again.`}
                  </p>
                </div>
              )}
              <p className="mb-2 text-xs text-neutral-blue-300">
                <span className="text-error-500">*</span> Required
              </p>

              <div className="grid max-h-[40vh] grid-cols-1 gap-5 overflow-y-auto pb-4 pl-2 pr-4 text-sm text-neutral-blue-400">
                {(() => {
                  const hasRenderableQuestions = (questions) =>
                    questions.some((question) => {
                      if (!getApplicationItem(question.key)) return false
                      return true
                    })

                  const hasGame = hasRenderableQuestions(gameQuestions)
                  const hasDriver = hasRenderableQuestions(driverQuestions)
                  const hasConnect = hasRenderableQuestions(connectQuestions)

                  return (
                    <>
                      {hasGame && (
                        <>
                          <ApplicationSection
                            label="Game"
                            sectionQuestions={gameQuestions}
                            formValues={formValues}
                            setFormValue={setFormValue}
                            accountsLoading={accountsLoading}
                            carClasses={carClasses}
                            platforms={platforms}
                            validationErrors={validationErrors}
                            showErrors={showErrors}
                          />
                          {(hasConnect || hasDriver) && (
                            <div className="divider my-1" />
                          )}
                        </>
                      )}

                      {hasDriver && (
                        <>
                          <ApplicationSection
                            label="Driver"
                            sectionQuestions={driverQuestions}
                            formValues={formValues}
                            setFormValue={setFormValue}
                            accountsLoading={accountsLoading}
                            validationErrors={validationErrors}
                            showErrors={showErrors}
                          />
                          {hasConnect && <div className="divider my-1" />}
                        </>
                      )}

                      {hasConnect && (
                        <ApplicationSection
                          label="Connect"
                          sectionQuestions={connectQuestions}
                          formValues={formValues}
                          setFormValue={setFormValue}
                          accountsLoading={accountsLoading}
                          validationErrors={validationErrors}
                          showErrors={showErrors}
                        />
                      )}
                    </>
                  )
                })()}

                {!hasQuestions && (
                  <p className="text-sm text-neutral-blue-300">
                    No application questions are required for this championship.
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="grid max-h-[48vh] grid-cols-1 gap-2 overflow-y-auto pr-1">
              {(futureEvents || []).map((event) => (
                <EventIntentRow
                  key={event.id}
                  championship={championship}
                  event={event}
                  eventIntent={eventIntentById[event.id] || 'registered'}
                  getIntentTriggerClass={getIntentTriggerClass}
                  setEventIntentById={setEventIntentById}
                />
              ))}

              {(futureEvents || []).length === 0 && (
                <p className="text-sm text-neutral-blue-300">
                  No live or upcoming events were found. You can still{' '}
                  {isApplicationFormEnabled
                    ? 'submit your application'
                    : 'confirm your registration'}
                  .
                </p>
              )}
            </div>
          )}

          <div className="divider" />

          <DialogFooter className="mt-4 gap-2 pt-2">
            {step === 2 &&
              hasQuestions &&
              (!canProceedFromStepOne || hasInteractiveQuestions) && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                >
                  Back
                </Button>
              )}

            {step === 1 ? (
              <Button type="button" onClick={onContinue}>
                Continue
              </Button>
            ) : (
              <SubmitButton
                isApplicationFormEnabled={isApplicationFormEnabled}
              />
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function EventIntentRow({
  championship,
  event,
  eventIntent,
  getIntentTriggerClass,
  setEventIntentById,
}) {
  const eventRegistrationOpen = canRecruitForEvent(championship, event)
  const registrationCountdown = useEventRegistrationCountdown(
    championship,
    event,
  )

  return (
    <div className="group flex flex-col gap-4 border-b border-neutral-blue-800 pb-4 last:border-0 md:flex-row md:items-center md:pb-3">
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <div className="relative aspect-video h-[48px] w-[85px] shrink-0 overflow-hidden rounded-md border border-neutral-blue-800 bg-neutral-blue-900 md:h-[54px] md:w-[96px]">
          <ImageWithFallback
            fill
            fallbackUrl="/img/default/header.webp"
            alt="Track Image"
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            src={
              event.customTrackImgUrl
                ? event.customTrackImgUrl
                : event.track?.coverImgUrl
            }
          />
        </div>

        <div className="flex min-w-0 flex-col">
          <p className="truncate text-sm font-semibold text-shades-0 md:text-base">
            {event.name}
          </p>

          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-neutral-blue-300">
            {dayjs(event.startDateTime).format('ddd D MMM • HH:mm z')}
            {!eventRegistrationOpen && (
              <span className="text-warning-400">Registration scheduled</span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-2 w-full shrink-0 md:mt-0 md:w-auto md:min-w-[160px]">
        {registrationCountdown.isScheduled ? (
          <div className="flex h-[46px] w-full cursor-not-allowed items-center rounded-sm border border-warning-500/25 bg-neutral-blue-800 px-4 py-3 text-sm font-medium text-warning-300">
            <span className="h-2 w-2 shrink-0 rounded-full bg-current opacity-80" />
            <span className="mx-auto shrink-0 text-xs tabular-nums text-neutral-blue-100">
              {registrationCountdown.label}
            </span>
          </div>
        ) : (
          <Select
            value={eventIntent}
            disabled={!eventRegistrationOpen}
            onValueChange={(value) => {
              setEventIntentById((prev) => ({
                ...prev,
                [event.id]: value,
              }))
            }}
          >
            <SelectTrigger
              className={`w-full border transition-colors ${getIntentTriggerClass(
                event.id,
              )}`}
            >
              <SelectValue placeholder="Select intent" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(eventIntentOptions).map(
                ([optionValue, option]) => (
                  <SelectItem
                    key={optionValue}
                    value={optionValue}
                    className={option.itemClass}
                  >
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-current opacity-80" />
                      {option.label}
                    </span>
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  )
}

function SubmitButton({ isApplicationFormEnabled }) {
  const { pending } = useFormStatus()

  return (
    <Button type="submit" disabled={pending}>
      {pending
        ? isApplicationFormEnabled
          ? 'Submitting...'
          : 'Joining...'
        : isApplicationFormEnabled
          ? 'Submit Application'
          : 'Confirm Registration'}
    </Button>
  )
}

function ApplicationSection({
  label,
  sectionQuestions,
  formValues,
  setFormValue,
  carClasses,
  platforms,
  accountsLoading,
  validationErrors,
  showErrors,
}) {
  const renderableQuestions = sectionQuestions.filter((question) => {
    if (!getApplicationItem(question.key)) return false
    return true
  })

  if (renderableQuestions.length === 0) {
    return null
  }

  return (
    <>
      <h2 className="text-lg font-semibold text-shades-0">{label}</h2>

      {renderableQuestions.map((question) => {
        const Component = getApplicationItem(question.key).component

        return (
          <Component
            key={question.key}
            applying
            formItems={formValues}
            setFormValue={setFormValue}
            applicationQuestion={question}
            accountsLoading={accountsLoading}
            autocomplete="off"
            carClasses={carClasses}
            platforms={platforms}
            validationErrors={validationErrors}
            showErrors={showErrors}
          />
        )
      })}
    </>
  )
}
